import { Octopus } from '@maximizer/core/shared/domain';
import { CaseCard, CaseFieldOption, CaseSummary } from '../../../models';
import { CaseSummaryResponse } from '../models';

function mapCompanyOrIndividual(entry?: Octopus.AbEntry): string {
  if (entry) {
    switch (entry.Type) {
      case Octopus.AbEntryType.Company:
        return entry.CompanyName;
      case Octopus.AbEntryType.Individual:
        return `${entry.FirstName} ${entry.LastName}`.trim();
    }
  }

  return '';
}

export class CaseCardMapper extends Octopus.ConfigurationMapper<
  Octopus.Case,
  CaseCard
> {
  override from(source: Octopus.Case): CaseCard {
    return {
      key: source.Key,
      parentKey: source.ParentKey,
      subject: source.Subject,
      caseNumber: source.CaseNumber,
      status: source.Status?.DisplayValue ?? null,
      priority: source.Priority?.DisplayValue ?? null,
      abEntry: {
        key: source.AbEntry.Key ?? null,
        companyName: mapCompanyOrIndividual(source.AbEntry),
      },
      contact: source.Contact
        ? {
            key: source.Contact.Key,
            firstName: source.Contact.FirstName,
            lastName: source.Contact.LastName,
          }
        : null,
      owner: source.Owner
        ? {
            value: source.Owner?.Value ?? '',
            display: source.Owner?.DisplayValue ?? '',
          }
        : null,
      assignedTo: source.AssignedTo
        ? {
            value: source.AssignedTo.Key?.Value ?? '',
            display: source.AssignedTo.DisplayValue ?? '',
          }
        : null,
      isSaved: false,
    };
  }
}

export class CaseSummaryMapper extends Octopus.ConfigurationMapper<
  CaseSummaryResponse,
  CaseSummary
> {
  override from(source: CaseSummaryResponse): CaseSummary {
    const mostRecentCase = source.Case?.Data?.length
      ? source.Case.Data[0]
      : null;
    const matchingCases = source['Case.Count']?.Data ?? [];

    const result: CaseSummary = {
      casesAmount: Math.max(matchingCases.length - 1, 0),
    };

    if (mostRecentCase) {
      result.case = {
        key: mostRecentCase.Key,
        subject: mostRecentCase.Subject,
        caseNumber: mostRecentCase.CaseNumber,
        description: mostRecentCase.Description,
        companyName: mapCompanyOrIndividual(mostRecentCase.AbEntry),
        status: mostRecentCase.Status?.DisplayValue ?? null,
        priority: mostRecentCase.Priority?.DisplayValue ?? null,
        contact: mostRecentCase.Contact
          ? {
              firstName: mostRecentCase.Contact.FirstName,
              lastName: mostRecentCase.Contact.LastName,
            }
          : null,
        assignedTo: mostRecentCase.AssignedTo?.DisplayValue ?? null,
        lastModifyDate: mostRecentCase.LastModifyDate?.Value,
      };
    }

    return result;
  }
}

export class CaseFieldOptionsMapper extends Octopus.ConfigurationMapper<
  Octopus.CaseFieldOptionsResponse,
  CaseFieldOption
> {
  from(source: Octopus.CaseFieldOptionsResponse): CaseFieldOption {
    const csCase = source.Case?.FieldOptions;

    return {
      status: csCase?.Status?.map(this.mapValueAndDisplay) ?? [],
      subject: csCase?.Subject?.map(this.mapValueAndDisplay) ?? [],
      product: csCase?.Product?.map(this.mapValueAndDisplay) ?? [],
      category: csCase?.Category?.map(this.mapValueAndDisplay) ?? [],
    };
  }
}
